// LayerThumbnailHub — エンジンのレイヤーサムネ (RGBA) を、画面に出ている小さな canvas へ
// 直接貼る橋渡し (layer-thumbnails-2026-10-13.md)。
//
// 描き味の約束:
// - 画素は React の state に入れない・パネルを再レンダーしない。層ごとの canvas を ref で
//   登録し、`putImageData` するだけ。
// - **自分が描いている間は届いても貼らない**。最新だけ保持し、手が止まったら貼る
//   (止まるまで短い間隔でしか見に行かない)。
// - 登録されている canvas が 1 つも無い (= サムネを出す面が無い) 間は、エンジンに
//   `setLayerThumbnailsVisible(false)` を伝えて作らせない。
// - エンジンの状態が 'off' / 'stopped' の間は、枠から `data-has-thumb` を外して番号だけに戻す。
//
// フレームワーク非依存の純クラス (node の vitest で偽の engine / canvas を渡して検証する)。

import type {
  LayerThumbnailMessage,
  LayerThumbnailState,
} from '../../../infrastructure/drawing/CanvasDrawingEngine.js';

/** Hub が engine に要求する面 (CanvasDrawingEngine の部分集合)。 */
export interface LayerThumbnailEngineLike {
  onLayerThumbnail(cb: (t: LayerThumbnailMessage) => void): () => void;
  onLayerThumbnailState(cb: (s: LayerThumbnailState) => void): () => void;
  setLayerThumbnailsVisible(visible: boolean): void;
  setLayerThumbnailsSuspended(suspended: boolean): void;
  /** 長押し強調 (任意: 無い engine では強調だけ何もしない)。 */
  beginLayerSolo?(layerId: number): boolean;
  endLayerSolo?(): void;
}

/** 貼り先 canvas の最小面。 */
export interface ThumbCanvasLike {
  width: number;
  height: number;
  getContext(type: '2d'): { putImageData(img: ImageData, x: number, y: number): void } | null;
}

/** 番号を載せる枠 (button) の最小面。 */
export interface ThumbFrameLike {
  readonly dataset: DOMStringMap | Record<string, string | undefined>;
  readonly style: { width: string; height: string };
}

/** 枠 (CSS px) に収める最大の辺。 */
export const LAYER_THUMB_BOX_PX = 36;

/** 保留中の貼り付けを見に行く間隔 (ms)。描いている間だけ回る軽いタイマー。 */
export const THUMB_PENDING_POLL_MS = 150;

interface Surface {
  readonly layerId: number;
  readonly canvas: ThumbCanvasLike;
  readonly frame: ThumbFrameLike;
}

export interface LayerThumbnailHubOptions {
  readonly engine: LayerThumbnailEngineLike;
  /** 自分のストローク中か (useRoomChrome の isStrokeActive)。 */
  readonly isDrawing: () => boolean;
  readonly makeImageData?: (rgba: Uint8ClampedArray, w: number, h: number) => ImageData;
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
}

/** 縦横比のまま `LAYER_THUMB_BOX_PX` の枠に収めた CSS px。 */
export const fitThumbBox = (w: number, h: number): { width: number; height: number } => {
  const m = Math.max(w, h);
  if (m <= 0) return { width: LAYER_THUMB_BOX_PX, height: LAYER_THUMB_BOX_PX };
  return {
    width: Math.max(1, Math.round((LAYER_THUMB_BOX_PX * w) / m)),
    height: Math.max(1, Math.round((LAYER_THUMB_BOX_PX * h) / m)),
  };
};

export class LayerThumbnailHub {
  private readonly engine: LayerThumbnailEngineLike;
  private readonly isDrawing: () => boolean;
  private readonly makeImageData: (rgba: Uint8ClampedArray, w: number, h: number) => ImageData;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  private readonly surfaces = new Set<Surface>();
  /** 層ごとの最新 (後から登録された面にも貼れるよう保持)。 */
  private readonly latest = new Map<number, LayerThumbnailMessage>();
  /** まだ貼っていない層 (描いている間に届いた分)。 */
  private readonly pending = new Set<number>();
  private state: LayerThumbnailState = 'off';
  private sentVisible: boolean | null = null;
  private visibleSyncQueued = false;
  private timer: unknown = null;
  private readonly stateListeners = new Set<(s: LayerThumbnailState) => void>();
  private readonly unsubs: Array<() => void> = [];
  private disposed = false;

  constructor(opts: LayerThumbnailHubOptions) {
    this.engine = opts.engine;
    this.isDrawing = opts.isDrawing;
    this.makeImageData =
      opts.makeImageData ??
      ((rgba, w, h) => new ImageData(rgba as Uint8ClampedArray<ArrayBuffer>, w, h));
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.unsubs.push(
      this.engine.onLayerThumbnailState((s) => this.onState(s)),
      this.engine.onLayerThumbnail((t) => this.onThumbnail(t)),
    );
  }

  /** 長押しで、その層だけを強調する。始められたら true。 */
  beginSolo(layerId: number): boolean {
    return this.engine.beginLayerSolo?.(layerId) ?? false;
  }

  /** 強調を終える (強調中でなければ何もしない)。 */
  endSolo(): void {
    this.engine.endLayerSolo?.();
  }

  /** いま画面にサムネを出している面があるか。 */
  hasSurface(): boolean {
    return this.surfaces.size > 0;
  }

  /** 表示用の状態 ('on' のときだけ画像を出す)。 */
  getState(): LayerThumbnailState {
    return this.state;
  }

  subscribeState(cb: (s: LayerThumbnailState) => void): () => void {
    this.stateListeners.add(cb);
    return () => {
      this.stateListeners.delete(cb);
    };
  }

  /**
   * 層の canvas + 枠を登録する。返り値で登録解除。登録の有無が `visible` になる
   * (面が無い間はエンジンがサムネを作らない)。
   */
  register(layerId: number, canvas: ThumbCanvasLike, frame: ThumbFrameLike): () => void {
    const surface: Surface = { layerId, canvas, frame };
    this.surfaces.add(surface);
    this.queueVisibleSync();
    if (this.latest.has(layerId)) {
      if (this.isDrawing()) this.markPending(layerId);
      else this.paint(surface, this.latest.get(layerId) as LayerThumbnailMessage);
    }
    return () => {
      this.surfaces.delete(surface);
      this.resetFrame(surface.frame);
      this.queueVisibleSync();
    };
  }

  /** パン中など「表示位置を動かしている間」をエンジンへ (自分のストローク中はエンジンが知っている)。 */
  setSuspended(suspended: boolean): void {
    if (this.disposed) return;
    this.engine.setLayerThumbnailsSuspended(suspended);
  }

  dispose(): void {
    this.disposed = true;
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
    for (const s of this.surfaces) this.resetFrame(s.frame);
    this.surfaces.clear();
    this.latest.clear();
    this.pending.clear();
    this.stateListeners.clear();
  }

  private onState(s: LayerThumbnailState): void {
    if (this.state === s) return;
    this.state = s;
    if (s !== 'on') {
      this.latest.clear();
      this.pending.clear();
      for (const surface of this.surfaces) this.resetFrame(surface.frame);
    }
    for (const cb of this.stateListeners) cb(s);
  }

  private onThumbnail(t: LayerThumbnailMessage): void {
    if (this.disposed || this.state !== 'on') return;
    this.latest.set(t.layerId, t);
    if (this.isDrawing()) {
      this.markPending(t.layerId);
      return;
    }
    this.pending.delete(t.layerId);
    for (const surface of this.surfaces) {
      if (surface.layerId === t.layerId) this.paint(surface, t);
    }
  }

  private markPending(layerId: number): void {
    this.pending.add(layerId);
    if (this.timer !== null) return;
    this.timer = this.setTimer(() => {
      this.timer = null;
      this.flushPending();
    }, THUMB_PENDING_POLL_MS);
  }

  private flushPending(): void {
    if (this.disposed || this.pending.size === 0) return;
    if (this.isDrawing()) {
      // まだ描いている: 触らず次に見に行く。
      const first = this.pending.values().next().value;
      if (first !== undefined) this.markPending(first);
      return;
    }
    const ids = [...this.pending];
    this.pending.clear();
    for (const id of ids) {
      const t = this.latest.get(id);
      if (t === undefined) continue;
      for (const surface of this.surfaces) {
        if (surface.layerId === id) this.paint(surface, t);
      }
    }
  }

  private paint(surface: Surface, t: LayerThumbnailMessage): void {
    if (this.state !== 'on') return;
    const { canvas, frame } = surface;
    if (canvas.width !== t.width) canvas.width = t.width;
    if (canvas.height !== t.height) canvas.height = t.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;
    ctx.putImageData(this.makeImageData(t.rgba, t.width, t.height), 0, 0);
    const box = fitThumbBox(t.width, t.height);
    const w = `${box.width}px`;
    const h = `${box.height}px`;
    if (frame.style.width !== w) frame.style.width = w;
    if (frame.style.height !== h) frame.style.height = h;
    (frame.dataset as Record<string, string | undefined>).hasThumb = 'true';
  }

  private resetFrame(frame: ThumbFrameLike): void {
    (frame.dataset as Record<string, string | undefined>).hasThumb = 'false';
    frame.style.width = '';
    frame.style.height = '';
  }

  private queueVisibleSync(): void {
    if (this.visibleSyncQueued || this.disposed) return;
    this.visibleSyncQueued = true;
    queueMicrotask(() => {
      this.visibleSyncQueued = false;
      if (this.disposed) return;
      const v = this.surfaces.size > 0;
      if (this.sentVisible === v) return;
      this.sentVisible = v;
      this.engine.setLayerThumbnailsVisible(v);
    });
  }
}
